/**
 * Pure helpers for the consumer feed's search + tag/cuisine filtering.
 *
 * Everything here is client-side over the already-loaded venue list — no
 * queries. Labels are resolved against the in-memory taxonomy vocabulary.
 */

import { labelFor } from "@/lib/taxonomyLabels";
import { coordsOf, haversineKm, type LatLng } from "@/lib/geo";
import type { CapacityLevel } from "@/lib/capacityLevel";
import type { Lang } from "@/i18n/translations";
import type { Restaurant } from "@/types/restaurant";
import type { Cuisine, Tag } from "@/types/taxonomy";

/** An active filter — typed so a cuisine slug and a tag slug never collide. */
export interface FilterToken {
  kind: "cuisine" | "tag";
  slug: string;
}

/** A chip in the "recommended" row: a taxonomy item + its localized label. */
export interface ChipOption {
  kind: "cuisine" | "tag";
  slug: string;
  label: string;
}

/** Lowercase + strip diacritics so "teras" matches "Terasă", "centru" "Centru". */
export function foldText(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

export function sameToken(a: FilterToken, b: FilterToken): boolean {
  return a.kind === b.kind && a.slug === b.slug;
}

export function hasToken(tokens: FilterToken[], t: FilterToken): boolean {
  return tokens.some((x) => sameToken(x, t));
}

/** Toggle a token in/out of the active set (returns a new array). */
export function toggleToken(tokens: FilterToken[], t: FilterToken): FilterToken[] {
  return hasToken(tokens, t) ? tokens.filter((x) => !sameToken(x, t)) : [...tokens, t];
}

/**
 * The N most-used taxonomy items across the given venues, cuisines + tags
 * merged and ranked by how many venues carry each. Unknown slugs (not in the
 * vocabulary) are skipped. Ties break by the vocabulary's own order.
 */
export function topTaxonomy(
  restaurants: Restaurant[],
  cuisines: Cuisine[],
  tags: Tag[],
  lang: Lang,
  n = 10,
): ChipOption[] {
  const countByKey = new Map<string, number>(); // key = `${kind}:${slug}`
  const bump = (kind: "cuisine" | "tag", slug: string) => {
    const key = `${kind}:${slug}`;
    countByKey.set(key, (countByKey.get(key) ?? 0) + 1);
  };
  for (const r of restaurants) {
    for (const slug of r.cuisine_slugs ?? []) bump("cuisine", slug);
    for (const slug of r.tag_slugs ?? []) bump("tag", slug);
  }

  // Build candidates from the vocabulary (so labels exist + order is stable),
  // carrying each item's venue-count; vocabulary order is the ordinal tiebreak.
  const candidates: { opt: ChipOption; count: number; ord: number }[] = [];
  let ord = 0;
  for (const c of cuisines) {
    const count = countByKey.get(`cuisine:${c.slug}`) ?? 0;
    if (count > 0) {
      candidates.push({ opt: { kind: "cuisine", slug: c.slug, label: labelFor(c, lang) }, count, ord: ord++ });
    }
  }
  for (const t of tags) {
    const count = countByKey.get(`tag:${t.slug}`) ?? 0;
    if (count > 0) {
      candidates.push({ opt: { kind: "tag", slug: t.slug, label: labelFor(t, lang) }, count, ord: ord++ });
    }
  }

  candidates.sort((a, b) => (b.count - a.count) || (a.ord - b.ord));
  return candidates.slice(0, n).map((c) => c.opt);
}

/**
 * Does the venue match the folded (lowercased, diacritic-stripped) search
 * query? Matches name, neighborhood, and the current-language cuisine + tag
 * labels — so typing a cuisine, a vibe, or an area all work.
 */
export function matchesQuery(
  r: Restaurant,
  foldedQuery: string,
  cuisines: Cuisine[],
  tags: Tag[],
  lang: Lang,
): boolean {
  if (!foldedQuery) return true;
  const haystack: string[] = [r.name];
  if (r.neighborhood) haystack.push(r.neighborhood);
  const cuisineSet = new Set(r.cuisine_slugs ?? []);
  const tagSet = new Set(r.tag_slugs ?? []);
  for (const c of cuisines) if (cuisineSet.has(c.slug)) haystack.push(labelFor(c, lang));
  for (const t of tags) if (tagSet.has(t.slug)) haystack.push(labelFor(t, lang));
  return haystack.some((s) => foldText(s).includes(foldedQuery));
}

// Tap-history scoring — tuned so the bias is a nudge, not a takeover: a venue
// the visitor actually opened outranks taste-alike venues, which outrank
// alphabetical strangers, and everything decays with a 14-day half-life.
const HALF_LIFE_DAYS = 14;
const DIRECT_TAP_WEIGHT = 3;
const SHARED_CUISINE_WEIGHT = 1;
const SHARED_TAG_WEIGHT = 0.5;

/**
 * Soft relevance score for the Recommended ordering, derived from the
 * on-device tap history (see lib/tapHistory.ts). Pure: same inputs → same
 * score. 0 = no history signal (falls through to the alphabetical order).
 */
export function scoreByHistory(
  r: Restaurant,
  history: import("@/lib/tapHistory").TapEntry[],
  now: number,
): number {
  if (history.length === 0) return 0;
  const cuisineSet = new Set(r.cuisine_slugs ?? []);
  const tagSet = new Set(r.tag_slugs ?? []);
  let score = 0;
  for (const entry of history) {
    const ageDays = Math.max(0, (now - entry.at) / 86_400_000);
    const weight = 0.5 ** (ageDays / HALF_LIFE_DAYS);
    if (entry.id === r.id) score += DIRECT_TAP_WEIGHT * weight;
    if (entry.cuisine_slugs.some((s) => cuisineSet.has(s))) score += SHARED_CUISINE_WEIGHT * weight;
    if (entry.tag_slugs.some((s) => tagSet.has(s))) score += SHARED_TAG_WEIGHT * weight;
  }
  return score;
}

/** AND match: the venue must carry every active token's slug. */
export function matchesTokens(r: Restaurant, tokens: FilterToken[]): boolean {
  if (tokens.length === 0) return true;
  const cuisineSet = new Set(r.cuisine_slugs ?? []);
  const tagSet = new Set(r.tag_slugs ?? []);
  return tokens.every((tok) =>
    tok.kind === "cuisine" ? cuisineSet.has(tok.slug) : tagSet.has(tok.slug),
  );
}

// -- Intent filters ----------------------------------------------------------
// The dimensions the feed already knows about but had no filter for: openness,
// live capacity, distance, a running offer, and the fast-venue tier. Kept out
// of `FilterToken` on purpose — tokens are taxonomy slugs, these are scalars,
// and the concierge parser (lib/nlSearch.ts) fills both from one sentence.

export interface IntentFilters {
  /** Only venues currently inside their opening hours. */
  openNow?: boolean;
  /** Live busy-ness ceiling — GREEN = wide open only, YELLOW = not packed. */
  capacityMax?: CapacityLevel;
  /** Only venues within this many km of the visitor's position. */
  maxDistanceKm?: number;
  /** Only venues with an offer running right now. */
  hasDiscount?: boolean;
  /** Only §28 "fast venue" tier venues. */
  fastVenue?: boolean;
}

/** Every intent dimension, in the order chips should render. */
export const INTENT_KEYS = [
  "openNow",
  "capacityMax",
  "hasDiscount",
  "fastVenue",
  "maxDistanceKm",
] as const;

export type IntentKey = (typeof INTENT_KEYS)[number];

export function hasIntent(intent: IntentFilters): boolean {
  return INTENT_KEYS.some((k) => intent[k] !== undefined);
}

/** Drop one dimension (returns a new object) — the chip's X button. */
export function clearIntentKey(intent: IntentFilters, key: IntentKey): IntentFilters {
  const next = { ...intent };
  delete next[key];
  return next;
}

/**
 * Everything `matchesIntent` needs that doesn't live on the venue row. The
 * caller (Home) owns these: openness comes from its 30 s tick, capacity from
 * the floor bus + the guest gate, discounts from the loaded offer map.
 */
export interface IntentContext {
  /** Venue is inside its opening hours (feed's own `isRestaurantOpen` result). */
  open: boolean;
  /** Guests never get capacity intents — same posture as RestaurantCard. */
  signedIn: boolean;
  /** Visitor position, or null when location is off/denied/unresolved. */
  coords: LatLng | null;
  /** A discount of this venue is inside its time window right now. */
  hasLiveDiscount: boolean;
  /** Live busy-ness, or null when this venue has no live capacity to show. */
  level: CapacityLevel | null;
}

const LEVEL_RANK: Record<CapacityLevel, number> = { GREEN: 0, YELLOW: 1, RED: 2 };

/**
 * AND match across the active intent dimensions. Absent dimensions never
 * filter. Two deliberate exclusions (truthful-claims rule): a capacity intent
 * drops every venue we have no live reading for — guests, POS-off venues — so
 * "not crowded" only ever returns venues the live data vouches for; and a
 * distance intent drops venues without coordinates.
 */
export function matchesIntent(
  r: Restaurant,
  intent: IntentFilters,
  ctx: IntentContext,
): boolean {
  if (intent.openNow === true && !ctx.open) return false;
  if (intent.hasDiscount === true && !ctx.hasLiveDiscount) return false;
  if (intent.fastVenue === true && r.is_fast_venue !== true) return false;

  if (intent.capacityMax !== undefined) {
    if (!ctx.signedIn || ctx.level === null) return false;
    if (LEVEL_RANK[ctx.level] > LEVEL_RANK[intent.capacityMax]) return false;
  }

  if (intent.maxDistanceKm !== undefined) {
    if (!ctx.coords) return false;
    const here = coordsOf(r);
    if (!here) return false;
    if (haversineKm(ctx.coords, here) > intent.maxDistanceKm) return false;
  }

  return true;
}
